import React, { useCallback, useEffect, useState } from "react";
import { getContentLimits, saveContentLimits } from "../../api/billing";
import { LoadState, SectionCard } from "../UserDetails/ui";
import type { ContentLimitAdminView, ContentLimitSettings, LimitedContentType } from "./types";
import { ErrorText, SaveButton, inputClass, useAction, useLoad } from "./ui";

const TYPES: ReadonlyArray<{ key: LimitedContentType; label: string }> = [
  { key: "still_image", label: "Still images" },
  { key: "carousel", label: "Carousels" },
  { key: "video", label: "Videos" },
  { key: "story", label: "Stories" },
  { key: "blog_post", label: "Blogs" },
];
const PERIODS = ["weekly", "monthly"] as const;
type Period = (typeof PERIODS)[number];
type MaxByPeriod = ContentLimitAdminView["max"];
const PERIOD_WORD: Record<Period, string> = { weekly: "week", monthly: "month" };

/** Form values as typed: "" = no limit. */
type Draft = Record<LimitedContentType, Record<Period, string>>;

const toDraft = (s: ContentLimitSettings): Draft => {
  const draft = {} as Draft;
  for (const { key } of TYPES) {
    draft[key] = {
      weekly: s[key].weekly === null ? "" : String(s[key].weekly),
      monthly: s[key].monthly === null ? "" : String(s[key].monthly),
    };
  }
  return draft;
};

/** Draft → settings, or an error message for the first invalid field. */
const fromDraft = (draft: Draft, max: MaxByPeriod): ContentLimitSettings | string => {
  const out = {} as ContentLimitSettings;
  for (const { key, label } of TYPES) {
    const values = { weekly: null as number | null, monthly: null as number | null };
    for (const period of PERIODS) {
      const raw = draft[key][period].trim();
      if (raw === "") continue;
      if (!/^\d{1,3}$/.test(raw) || Number(raw) < 1 || Number(raw) > max[period]) {
        return `${label} per ${PERIOD_WORD[period]}: enter a whole number from 1 to ${max[period]}, or leave empty for no limit.`;
      }
      values[period] = Number(raw);
    }
    if (values.weekly !== null && values.monthly !== null && values.weekly > values.monthly) {
      return `${label}: the weekly limit can't be higher than the monthly limit.`;
    }
    out[key] = values;
  }
  return out;
};

/** Global weekly/monthly limits per brand on every content type (still images, carousels, videos, stories, blogs) — applies to every user. */
export const ContentLimitsTab: React.FC = () => {
  const load = useCallback(() => getContentLimits(), []);
  const { data, loading, error, reload } = useLoad(load);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);
  const { busy, error: saveError, setError, run } = useAction();

  useEffect(() => {
    if (data) setDraft(toDraft(data.limits));
  }, [data]);

  if ((loading && !data) || error) return <LoadState loading={loading && !data} error={error} onRetry={reload} />;
  if (!draft || !data) return null;
  const { max } = data;

  const update = (type: LimitedContentType, period: Period, value: string) => {
    setSaved(false);
    setDraft({ ...draft, [type]: { ...draft[type], [period]: value } });
  };

  const save = async () => {
    setSaved(false);
    const parsed = fromDraft(draft, max);
    if (typeof parsed === "string") return setError(parsed);
    const done = await run(async () => {
      const result = await saveContentLimits(parsed);
      setDraft(toDraft(result.limits));
    });
    if (done) setSaved(true);
  };

  return (
    <SectionCard title="Content limits per brand">
      <p className="mb-4 text-sm text-gray-500">
        How many of each content type one brand can have. Applies to every user on every plan, from the next topic or
        post they create. Leave a box empty for no limit. The most you can set is {max.weekly} a
        week and {max.monthly} a month. Blogs count both calendar blog topics and blogs made on the Blogs page. Weeks run Monday–Sunday (UTC); months
        are calendar months. Lowering a limit never removes topics that already exist.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full max-w-xl text-sm">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500">
              <th className="py-2 pr-4">Content type</th>
              <th className="py-2 pr-4">Per week (max {max.weekly})</th>
              <th className="py-2">Per month (max {max.monthly})</th>
            </tr>
          </thead>
          <tbody>
            {TYPES.map(({ key, label }) => (
              <tr key={key} className="border-t border-gray-100">
                <td className="py-2 pr-4 font-medium text-gray-800">{label}</td>
                {PERIODS.map((period) => (
                  <td key={period} className={period === "weekly" ? "py-2 pr-4" : "py-2"}>
                    <input
                      type="number"
                      min={1}
                      max={max[period]}
                      inputMode="numeric"
                      aria-label={`${label} per ${PERIOD_WORD[period]}`}
                      className={inputClass}
                      placeholder="No limit"
                      value={draft[key][period]}
                      disabled={busy}
                      onChange={(e) => update(key, period, e.target.value)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <SaveButton busy={busy} onClick={save}>
          Save limits
        </SaveButton>
        {saved && <p className="text-sm text-emerald-700">Limits saved. They apply to every user now.</p>}
      </div>
      <div className="mt-2">
        <ErrorText message={saveError} />
      </div>
    </SectionCard>
  );
};
